import InfoText from '@/components/common/InfoText';
import PlentySpinner from '@/components/common/PlentySpinner';
import { Header, Text } from '@/components/common/Typography';
import { useMutation } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { Goal } from '@common/goals';
import {
    AllFinancialEntitiesDocument,
    OpenPlentyGoalAccountDocument,
} from '@common/graphql/generated';
import { useAtomicEnabled, useAtomicStatus } from '@common/react/atomic';
import {
    getContributingOrInvestingStr,
    getContributionOrInvestmentStr,
    getMyGoalAccount,
} from '@common/react/goals';
import pluralize from '@common/util/pluralize';
import { useState } from 'react';
import GoalOnboardingConfirmKeyValueStack from './GoalOnboardingConfirmKeyValueStack';
import { useGoalOnboardingContext } from './GoalOnboardingContext';
import GoalOnboardingLayout from './GoalOnboardingLayout';

interface GoalOnboardingConfirmProps extends FlexProps {
    onComplete: () => void;
    onBack: () => void;
}

const GoalOnboardingConfirm = ({
    onComplete,
    onBack,
    ...props
}: GoalOnboardingConfirmProps) => {
    const atomicStatus = useAtomicStatus();
    const { isAtomicEnabled, atomicDisabledMessage } = useAtomicEnabled();
    const {
        goal,
        goalOnboardingData,
        onClose,
        isUpdating,
        updateGoalOnboarding,
        setStep,
        resetGoalOnboardingData,
    } = useGoalOnboardingContext();
    const [isOpeningAccount, setIsOpeningAccount] = useState(false);
    const [openGoalAccount] = useMutation(OpenPlentyGoalAccountDocument);

    if (!atomicStatus) return null;

    // Show disabled state if Atomic is not enabled
    if (!isAtomicEnabled) {
        return (
            <GoalOnboardingLayout
                footer={{
                    cta: {
                        text: 'Go back',
                        onComplete: onBack,
                        isSubmitting: false,
                    },
                    back: { onBack },
                }}
                {...props}
            >
                <Header fontWeight="medium" mb={2}>
                    Investment operations temporarily unavailable
                </Header>
                <Text color="textMuted" mb={8}>
                    {atomicDisabledMessage}
                </Text>
                <InfoText iconName="AlertCircle" mt={6}>
                    You can still view your existing goals and portfolio. New
                    account openings and investments will be available again
                    soon.
                </InfoText>
            </GoalOnboardingLayout>
        );
    }

    const submitConfirm = () => {
        if (!goalOnboardingData.fundingAccountId) return;
        setIsOpeningAccount(true);

        openGoalAccount({
            variables: {
                goalId: goal.id,
                fundingAccountId: goalOnboardingData.fundingAccountId,
                initialDepositPlan: goalOnboardingData.initialDepositPlan,
                transactionSchedulePlan:
                    goalOnboardingData.transactionSchedulePlan.amount > 0
                        ? goalOnboardingData.transactionSchedulePlan
                        : undefined,
            },
            // Refetch to retrieve new goal account financial entity data
            refetchQueries: [AllFinancialEntitiesDocument],
        })
            .then((res) => {
                const maybeGoal = res.data?.openPlentyGoalAccount?.query?.goal;
                const goal = maybeGoal ? (maybeGoal as Goal) : undefined;
                if (!goal) return;

                const account = getMyGoalAccount(goal);
                if (!account) {
                    // If account was not created, reset view state with up-to-date
                    // goal info. The user is most likely pending IDV approval,
                    // and this view should be shown in a "confirmed" state.
                    resetGoalOnboardingData(goal);
                }

                onComplete();
            })
            .finally(() => {
                setIsOpeningAccount(false);
            });
    };

    const cancelPlans = () => {
        updateGoalOnboarding({
            params: {
                confirmedAt: null,
            },
            resetData: true,
        }).then(() => setStep('PORTFOLIO'));
    };

    const isIDVComplete = atomicStatus?.status === 'ACCOUNT_COMPLETE';

    const { investmentType } = goalOnboardingData;
    assert(investmentType, 'Missing investmentType');

    const numTransfers =
        (goalOnboardingData.initialDepositPlan > 0 ? 1 : 0) +
        (goalOnboardingData.transactionSchedulePlan.amount > 0 ? 1 : 0);

    const contributionOrInvestmentStr =
        getContributionOrInvestmentStr(investmentType);
    const contributingOrInvestingStr =
        getContributingOrInvestingStr(investmentType);

    const ctaText = goalOnboardingData.isConfirmed
        ? 'Looks good'
        : numTransfers === 0
          ? 'Open goal'
          : isIDVComplete
            ? `Start ${contributingOrInvestingStr}`
            : `Set up ${contributionOrInvestmentStr}`;

    const ctaOnComplete = () => {
        if (goalOnboardingData.isConfirmed) {
            onClose();
        } else {
            submitConfirm();
        }
    };

    const titleActionPhrase =
        numTransfers === 0 ? 'initiate your transfers' : 'open your goal';
    const title = goalOnboardingData.isConfirmed
        ? goal.accountStructure === 'JOINT'
            ? `Your goal is confirmed. We’ll ${titleActionPhrase} as soon as your Joint account is approved.`
            : `Your goal is confirmed. We’ll ${titleActionPhrase} as soon as your identity is verified.`
        : `Last step - review your investment goal`;

    const subtitle = goalOnboardingData.isConfirmed
        ? goal.accountStructure === 'JOINT'
            ? 'Our partner, BNY Mellon | Pershing usually takes 1-2 business days to approve a new Joint account after both parties have signed. We’ll let you know once your account is approved, or if we have additional questions.'
            : 'Our partner, BNY Mellon | Pershing usually takes 1-2 business days to verify your identity. We’ll let you know once your account is approved, or if we have additional questions.'
        : `Click anything you’d like to change. When you’re ready, click ‘${ctaText}’.`;

    const secondary = goalOnboardingData.isConfirmed
        ? {
              text: 'Edit plan',
              onClick: cancelPlans,
          }
        : undefined;

    if (isOpeningAccount) {
        const dynamicLines = [
            'Creating your goal account',
            'Checking funding account balance',
            'Finalizing your goal',
        ];

        return <PlentySpinner dynamicLines={dynamicLines} />;
    }

    return (
        <GoalOnboardingLayout
            footer={{
                cta: {
                    text: ctaText,
                    onComplete: ctaOnComplete,
                    isSubmitting: isOpeningAccount || isUpdating,
                },
                secondary,
                back: { onBack },
            }}
            {...props}
        >
            <Header fontWeight="medium" mb={2}>
                {title}
            </Header>
            <Text color="textMuted" mb={8}>
                {subtitle}
            </Text>
            <GoalOnboardingConfirmKeyValueStack />
            {numTransfers > 0 &&
                !goalOnboardingData.isConfirmed &&
                !isIDVComplete && (
                    <InfoText iconName="AlertCircle" mt={6}>
                        You can set up your contribution plan while your account
                        is being approved. Your {contributionOrInvestmentStr}{' '}
                        will only be transferred after your account is approved.
                    </InfoText>
                )}
            {numTransfers > 0 && goalOnboardingData.isConfirmed && (
                <InfoText iconName="AlertCircle" mt={6}>
                    Your {pluralize('transfer', numTransfers, false)} will only
                    be initiated after your account is approved.
                </InfoText>
            )}
        </GoalOnboardingLayout>
    );
};

export default GoalOnboardingConfirm;
